import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Close, IosShare } from '@mui/icons-material';
import { LD } from './tokens';

interface CompactHeaderProps {
  thumbnail?: string;
  street: string;
  summary: string;
  onShare: () => void;
  onClose?: () => void;
}

/**
 * Replaces the photo once it scrolls away: which home this is, plus share
 * and close, always in reach. Rendered inside a zero-height sticky slot so it
 * overlays the page without pushing it down.
 */
const CompactHeader: React.FC<CompactHeaderProps> = ({
  thumbnail,
  street,
  summary,
  onShare,
  onClose,
}) => (
  <Box
    data-testid="listing-compact-header"
    sx={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 52,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      px: '12px',
      bgcolor: 'white',
      borderBottom: `1px solid ${LD.line}`,
      '@keyframes listingHeaderIn': {
        from: { opacity: 0, transform: 'translateY(-8px)' },
        to: { opacity: 1, transform: 'none' },
      },
      animation: 'listingHeaderIn 0.18s ease-out',
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}
  >
    {thumbnail && (
      <Box
        component="img"
        src={thumbnail}
        alt=""
        sx={{
          width: 44,
          height: 33,
          borderRadius: '6px',
          objectFit: 'cover',
          flexShrink: 0,
        }}
      />
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        noWrap
        sx={{
          fontWeight: 700,
          fontSize: '14.5px',
          color: LD.ink,
          lineHeight: 1.4,
        }}
      >
        {street}
      </Typography>
      <Typography
        noWrap
        sx={{ fontSize: '12.5px', color: LD.muted, lineHeight: 1.4 }}
      >
        {summary}
      </Typography>
    </Box>
    <IconButton
      onClick={onShare}
      aria-label="Share this home"
      sx={{ width: 40, height: 40, color: LD.ink2 }}
    >
      <IosShare sx={{ fontSize: 20 }} />
    </IconButton>
    {onClose && (
      <IconButton
        onClick={onClose}
        aria-label="Close property details"
        sx={{ width: 40, height: 40, color: LD.ink2, ml: '-2px' }}
      >
        <Close sx={{ fontSize: 22 }} />
      </IconButton>
    )}
  </Box>
);

export default CompactHeader;
